import _, { cloneDeep, set } from 'lodash';
import {
  Container,
  DisplayObject,
  FederatedPointerEvent,
  Graphics,
  Rectangle,
  RoundedRectangle,
  Sprite,
  Text,
  Texture,
} from 'pixi.js';
import {
  Coords,
  DragParams,
  ShapeControlPointPosition,
  ShapeDragParams,
  ShapeSelectParams,
} from '../../resource/types/shape.type';
import { GeneralShape } from '../../../element-editor/shape/shapes/general/general-shape';
import { RectangleElement } from '../../../element-editor/shape/shapes/primitive/rectangle-element';
import {
  DragResponse,
  PointController,
} from '../point-controller/point-controller';
import { BaseVector, Vector } from '../../base/vector/vector';
import { ImportedShape } from '../../../element-editor/shape/shapes/general/imported/imported-shape';
import { CurveCoverElement } from '../../../element-editor/shape/shapes/primitive/curve-cover-element';
import { StudioSceneShape } from '../../../element-editor/shape/shapes/general/root/studio-scene-shape';
import { StudioTextShape } from '../../../element-editor/store/studio/text/studio-text-shape';
import { VideoShape } from '../../../element-editor/shape/shapes/base/video-shape';
import { StudioShape } from '../../../element-editor/store/studio/shapes/studio-shape';

// Replace these with the real ones from your font export.
const MI_CODEPOINTS = {
  image: '\u{E3F4}', // <-- verify
  smart_display: '\u{F06E}', // <-- verify
} as const;

// If your BitmapText build doesn't support `anchor`, this helper centers via pivot.
function centerAnchorLike(o: any) {
  if (o.anchor?.set) {
    o.anchor.set(0.5);
  } else {
    // ensure metrics are up to date
    // @ts-ignore
    o.updateText?.();
    const w = o.width ?? 0;
    const h = o.height ?? 0;
    o.pivot.set(w / 2, h / 2);
  }
}
export type RCPoint =
  | 'left-top'
  | 'left-bottom'
  | 'right-top'
  | 'right-bottom'
  | 'center-bottom'
  | 'center'
  | 'center-top'
  | 'left-center'
  | 'right-center';

export interface RectangleDragResponse {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  dx?: number;
  dy?: number;
  dw?: number;
  dh?: number;
  pcDx?: number;
  pcDy?: number;
  dAngle?: number;
  point?: RCPoint;
  angle?: number;
  scale?: number;
}

export interface RectangleControllerConfig {
  id?: string;
  offset?: Coords;
  position?: Coords;
  selectedContainerMode?: boolean;
  width: number;
  height: number;
  symmetric?: boolean;
  centerBased?: boolean;
  showRectangle?: boolean;
  bottomDrag?: boolean;
  noScale?: boolean;
  rotation?: {
    angle: number;
    origin?: Coords;
    point?: ShapeControlPointPosition;
  };
  hiddenMode?: boolean;
  moveRectangle?: boolean;
  scaleMode?: boolean;
  startDrag?: (point?: RCPoint) => void;
  drag: (resp: RectangleDragResponse) => void;
  endDrag: (point?: RCPoint, params?: DragParams) => void;
  clicked?: () => void;
  doubleClicked?: () => void;
  mouseout?: () => void;
  mouseover?: () => void;
  noRect?: boolean;
  noRectRefresh?: boolean;
  noDrag?: boolean;
  dragParams?: ShapeDragParams;
  _startDrag?: () => void;
  _drag?: (dx?: number, dy?: number) => void;
  _endDrag?: () => void;
  noFill?: boolean;
  noStroke?: boolean;
  fill?: boolean;

  fillOpacity?: number;
  skipRC?: Partial<Record<RCPoint, boolean>>;
  skipPCs?: boolean;
  rectangleIndex?: number;
  'stroke-width'?: number;
  onTextIconClick?: () => void;
  onImageIconClick?: () => void;
  onAnimatedImagesIconClick?: () => void;
}

export class RectangleController {
  pcLeftTop: PointController;
  pcLeftCenter: PointController;
  pcLeftBottom: PointController;
  pcCenterTop: PointController;
  pcCenter: PointController;
  pcCenterBottom: PointController;
  pcRightTop: PointController;
  pcRightCenter: PointController;
  pcRightBottom: PointController;
  rect: RectangleElement;
  unMovedLengths: Coords;
  diff: Coords;
  currentLengths: Coords;

  mouseoutClear: boolean;

  hitAreaMargin = 8;
  get cs() {
    return this.parent.cs;
  }

  get service() {
    return this.parent.service;
  }

  // get os() {
  //   return this.parent.orientationService;
  // }

  get rotation() {
    return this.config.rotation;
  }

  get width() {
    return this.config.width;
  }

  set width(val: number) {
    this.config.width = val;
  }

  get height() {
    return this.config.height;
  }

  set height(val: number) {
    this.config.height = val;
  }

  get position() {
    return [this.offsetX, this.offsetY];
  }

  get strokeWidth() {
    return (this.config['stroke-width'] || 2) / this.cs.canvasScale;
  }

  get x() {
    return this.config.position[0];
  }

  set x(val: number) {
    this.config.position[0] = val;
  }

  get y() {
    return this.config.position[1];
  }
  set y(val: number) {
    this.config.position[1] = val;
  }

  get offsetX() {
    return this.config.offset?.[0] || 0;
  }

  get offsetY() {
    return this.config.offset?.[1] || 0;
  }

  set offsetX(val: number) {
    this.config.offset[0] = val;
  }

  set offsetY(val: number) {
    this.config.offset[1] = val;
  }

  get pcs() {
    return [
      this.pcLeftTop,
      this.pcLeftCenter,
      this.pcLeftBottom,
      this.pcCenterTop,
      this.pcCenter,
      this.pcCenterBottom,
      this.pcRightTop,
      this.pcRightCenter,
      this.pcRightBottom,
    ].filter(rc => !!rc);
  }

  get symmetric() {
    return this.config.symmetric;
  }

  get centerBased() {
    return this.config.centerBased || this.cs.isShiftPressed;
  }

  get scaleMode() {
    return this.config.scaleMode;
  }

  unMovedX: number;
  unMovedY: number;
  dx: number;
  dy: number;
  dw: number;
  dh: number;
  dragResponse: RectangleDragResponse;
  get noScale() {
    return this.config.noScale;
  }

  get skipRC() {
    return this.config.skipRC || {};
  }

  get canvasScale() {
    return this.noScale ? 1 : this.cs.canvasScale;
  }

  get inprogress() {
    return !!this.pcs.find(pc => pc.inProgress);
  }

  get selectedContainerMode() {
    return this.config.selectedContainerMode;
  }

  get hiddenMode() {
    return this.config.hiddenMode;
  }

  pcDx: number;
  pcDy: number;
  leftTop: Vector;
  leftBottom: Vector;
  rightTop: Vector;
  rightBottom: Vector;
  shapePosition: Vector;
  // The left-top vector's new position wrt. to the original origin through rotation-drag
  newLeftTop: Vector;
  newShapePosition: Vector;
  newOrigin: Coords;
  dLeftTop: Coords;
  unMovedAngle: number;
  unMoved: Coords;
  unMovedDiagonal: number;
  rotationMode = false;
  id: string;
  bottomElement: CurveCoverElement;
  topElement: CurveCoverElement;
  container: Container;
  // Shape icons
  shapeIconsContainer: Container;
  textIcon: Text;
  imageIcon: Sprite;
  animatedImagesIcon: Sprite;

  auxRectangle: RectangleElement;
  rcMouseOver = false;
  constructor(
    public parent:
      | GeneralShape
      | StudioSceneShape
      | StudioTextShape
      | VideoShape,
    public config: RectangleControllerConfig,
    private circleContainer: Container,
    private auxCircleContainer: Container,
    rectangleContainer?: Container,
  ) {
    this.config.position ||= [0, 0];
    this.id = Math.random().toString();
    this.container = new Container();
    (rectangleContainer || circleContainer)?.addChild(this.container);

    this.container.setTransform(this.offsetX, this.offsetY);
    // -- //
    // this.auxRectangle = new RectangleElement(this, this.auxCircleContainer, {
    //   position: {
    //     x: this.offsetX - 4,
    //     y: this.offsetY - 4,
    //   },
    //   width: this.width + 8,
    //   height: this.height + 8,
    //   fill: '#000000',
    //   'fill-opacity': 0.001,
    // })
    //   .mouseover(() => {
    //     // this.rcMouseOver = true;
    //   })
    //   .mouseout(() => {
    //     // this.rcMouseOver = false;
    //   });
    // -- //
    this.rect = new RectangleElement(
      this,
      this.container,
      {
        // position: {
        //   x: this.offsetX,
        //   y: this.offsetY,
        // },
        width: this.width,
        height: this.height,
        stroke: '#cccccc',
        'stroke-width': this.strokeWidth,
        // stroke: this.config.noStroke ? undefined : '#0191f3',
        //'stroke-width': this.hiddenMode ? 0 : 1,
        fill: this.config.fill ? '#000000' : undefined,
        // fill: this.config.fill ? '#532d2dff' : undefined,
        'fill-opacity': this.config.fill ? 0.001 : 0,
        noFill: this.config.noFill || !this.config.fill,
      },
      this.config.rectangleIndex,
    )
      .click(() => {
        this.config.clicked?.();
      })
      .doubleclick(() => {
        this.config.doubleClicked?.();
      })
      .mouseover(() => {
        this.rectMouseOver = true;
        // if (this.rcMouseOver) {
        //   return;
        // }
        // console.log('rc.rect > mouseover');
        this.isMouseOver = true;
        this.mouseoutClear = true;
        this.mouseover();
        // setTimeout(() => (this.mouseoutClear = false), 10);
      })
      .mouseout(() => {
        this.rectMouseOver = false;
        // console.log('rc.rect > mouseout');
        this.isMouseOver = false;
        // if (this.mouseoutClear) {
        //   return;
        // }
        this.mouseout();
      });

    this.rect.element.hitArea = new Rectangle(
      -this.hitAreaMargin,
      -this.hitAreaMargin,
      this.width + this.hitAreaMargin * 2,
      this.height + this.hitAreaMargin * 2,
    ); // or expanded padding

    // if (this.config.bottomDrag) {
    // this.topElement = new CurveCoverElement(
    //   this,
    //   this.container,
    //   {
    //     hoverItem: {
    //       type: 'line',
    //       x: this.width,
    //       y: 0,
    //       height: 8,
    //     },
    //     fill: '#000000',
    //     'fill-opacity': 0.001,
    //   },
    //   this.config.rectangleIndex,
    // )
    //   .drag(
    //     (dx, dy) => this.drag('center-top', { dx: 0, dy }),
    //     () => this.startDrag('center-top'),
    //     () => this.endDrag('center-top'),
    //   )
    //   .mouseover(() => {
    //     setTimeout(() => (this.mouseoutClear = false), 10);
    //     // console.log('mouseover');
    //     this.topElement.element.cursor = 'ns-resize';
    //   })
    //   .mouseout(() => {
    //     // console.log('mouseout');
    //     this.topElement.element.cursor = 'default';
    //     // console.log('mouseout - ', this.mouseoutClear);
    //     if (this.mouseoutClear) {
    //       return;
    //     }
    //     this.mouseoutClear = false;
    //     this.mouseout();
    //   });

    // this.bottomElement = new CurveCoverElement(
    //   this,
    //   this.container,
    //   {
    //     hoverItem: {
    //       type: 'line',
    //       x: this.width,
    //       y: 0,
    //       height: 8,
    //     },
    //     fill: '#000000',
    //     'fill-opacity': 0.001,
    //   },
    //   this.config.rectangleIndex,
    // )
    //   .drag(
    //     (dx, dy) => this.drag('center-bottom', { dx: 0, dy }),
    //     () => this.startDrag('center-bottom'),
    //     () => this.endDrag('center-bottom'),
    //   )
    //   .mouseover(() => {
    //     setTimeout(() => (this.mouseoutClear = false), 10);
    //     this.bottomElement.element.cursor = 'ns-resize';
    //   })
    //   .mouseout(() => {
    //     this.bottomElement.element.cursor = 'default';
    //     if (this.mouseoutClear) {
    //       return;
    //     }
    //     this.mouseoutClear = false;
    //     this.mouseout();
    //   });
    // }

    if (this.config.hiddenMode) {
      this.rect.patch({
        'stroke-width': this.strokeWidth,
      });
    }
    if (!this.config.noDrag) {
      // -- // -- //
      this.rect.drag(
        (dx, dy) => {
          this.dx = dx;
          this.dy = dy;
          (this.parent as StudioShape).drag(dx, dy);
        },
        () => {
          // -- // -- // -- //
          this.unMoved = [this.offsetX, this.offsetY];
          (this.parent as StudioShape).startBaseDrag();
        },
        () => {
          // this.offsetX += this.dx;
          // this.offsetY += this.dy;
          (this.parent as StudioShape).endDrag();
        },
      );
    }

    if (this.rotation) {
      this.leftTop = new BaseVector([-this.width / 2, -this.height / 2]);
      this.leftBottom = new BaseVector([-this.width / 2, this.height / 2]);

      this.rightTop = new BaseVector([this.width / 2, -this.height / 2]);
      this.rightBottom = new BaseVector([this.width / 2, this.height / 2]);

      [this.leftTop, this.leftBottom, this.rightTop, this.rightBottom].map(v =>
        v.rotate(this.rotation.angle),
      );

      this.shapePosition = this.leftTop.copy();
      this.shapePosition.rotate(-this.rotation.angle);
    }

    this.diff = [0, 0];
    this.currentLengths = cloneDeep([this.width, this.height]);

    this.pcLeftTop = this.initPC('left-top', 0, 0);
    this.pcLeftCenter = this.initPC('left-center', 0, this.height / 2);
    this.pcLeftBottom = this.initPC('left-bottom', 0, this.height);

    // if (!this.skipRC['center']) {
    //   this.pcCenter = new PointController(
    //     this,
    //     {
    //       p: [this.offsetX + this.width / 2, this.offsetY + this.height / 2],
    //       showCircle: true,
    //       noScale: this.noScale,
    //       // rotation: this.rotation,
    //       noConstraint: this.selectedContainerMode,
    //       controlPoint: {
    //         shapeIRI: this.parent.IRI,
    //         name: 'center',
    //       },
    //       start: () => {
    //         if (this.selectedContainerMode) {
    //           this.unMovedX = this.service.groupRCContainer.x;
    //           this.unMovedY = this.service.groupRCContainer.y;
    //           this.service.startDrag();
    //         }
    //         // else {
    //         //   this.parent.startBaseDrag();
    //         // }
    //       },
    //       drag: ({ dx, dy }) => {
    //         if (this.selectedContainerMode) {
    //           this.service.drag(dx, dy);
    //           this.service.groupRCContainer.setTransform(
    //             this.unMovedX + dx * this.cs.canvasScale,
    //             this.unMovedY + dy * this.cs.canvasScale,
    //           );
    //         }
    //         //  else {
    //         //   this.parent.drag(dx, dy);
    //         // }
    //         // this.pcCenter.patch({
    //         //   p: [this.x + this.width / 2, this.y + this.height / 2],
    //         // });
    //       },
    //       end: () =>
    //         this.selectedContainerMode ? this.service.endDrag() : null,
    //       mouseout: () => this.pcMouseOut(),
    //       mouseover: () => this.mouseover(),
    //     },
    //     circleContainer,
    //     auxCircleContainer,
    //   );
    // }

    this.pcCenterTop = this.initPC('center-top', this.width / 2, 0);
    this.pcCenterBottom = this.initPC(
      'center-bottom',
      this.width / 2,
      this.height,
    );

    this.pcRightTop = this.initPC('right-top', this.width, 0);
    this.pcRightCenter = this.initPC(
      'right-center',
      this.width,
      this.height / 2,
    );
    this.pcRightBottom = this.initPC('right-bottom', this.width, this.height);

    this.saveUnmoved();
    this.zoomUpdate();

    if (this.config.hiddenMode) {
      this.hide();
    }
  }

  isCornerPoint(point: RCPoint) {
    switch (point) {
      case 'left-top':
      case 'left-bottom':
      case 'right-top':
      case 'right-bottom':
        return true;
      default:
        return false;
    }
  }

  pcMouseOver = false;
  rectMouseOver = false;
  initPC(point: RCPoint, w: number, h: number) {
    if (this.skipRC[point] || this.config.skipPCs) {
      return;
    }

    let angle: number;
    let cursorType: string;

    switch (point) {
      case 'left-top':
      case 'right-bottom':
        angle = Math.atan(this.height / this.width);
        cursorType = 'nwse-resize';
        break;
      case 'left-bottom':
      case 'right-top':
        angle = Math.atan(-this.height / this.width);
        cursorType = 'nesw-resize';
        break;
      case 'center-top':
      case 'center-bottom':
        cursorType = 'ns-resize';
        break;
      case 'left-center':
      case 'right-center':
        cursorType = 'ew-resize';
        break;
      default:
        cursorType = 'move';
        break;
    }

    return new PointController(
      this,
      {
        p: [w, h],
        color: '#cccccc',
        showCircle: true,
        cursorType,
        // rotation: this.rotation,
        angle: this.scaleMode && this.isCornerPoint(point) ? angle : undefined,
        noScale: this.noScale,
        start: () => this.startDrag(point),
        drag: resp => this.drag(point, resp),
        end: () => this.endDrag(point),
        mouseover: () => {
          this.pcMouseOver = true;
          if (this.hiddenMode) {
            this.pcs.forEach(pc => pc.showAsGhost(0.5));
          }
        },
        mouseout: () => {
          this.pcMouseOver = false;
          setTimeout(() => {
            if (this.rectMouseOver || this.dragIsInProgress) {
              return;
            }
            if (this.hiddenMode) {
              this.pcs.forEach(pc => pc.showAsGhost());
            }
            this.config.mouseout?.();
          }, 1);
        },
      },
      this.container,
      this.container,
      // this.circleContainer || this.parent?.circleContainer,
      // this.auxCircleContainer || this.parent?.auxCircleContainer,
    );
  }

  dragIsInProgress = false;
  startDrag(point?: RCPoint) {
    this.dragIsInProgress = true;

    this.dx = 0;
    this.dy = 0;
    this.dw = 0;
    this.dh = 0;
    this.unMovedX = this.parent?.x || 0;
    this.unMovedY = this.parent?.y || 0;

    if (this.scaleMode) {
      this.unMovedDiagonal = Math.sqrt(
        Math.pow(this.width, 2) + Math.pow(this.height, 2),
      );
    }
    // -- // -- //
    if (this.rotation?.origin !== undefined || this.cs.isPressed('r')) {
      let end: Coords;
      this.cs.consumeKeyEvent('r');
      this.rotationMode = true;
      // -- // -- // -- // -- //
      switch (point) {
        case 'right-top':
          end = [this.width, 0];
          break;
        case 'right-center':
          end = [this.width, this.height / 2];
          break;
        case 'right-bottom':
          end = [this.width, this.height];
          break;
        case 'left-top':
          end = [0, 0];
          break;
        case 'left-center':
          end = [0, this.height / 2];
          break;
        case 'left-bottom':
          end = [0, this.height];
          break;
        case 'center-top':
          end = [this.width / 2, 0];
          break;
        case 'center-bottom':
          end = [this.width / 2, this.height];
          break;
      }

      // const start = this.config.rot //
      let start: Coords;

      if (this.rotation?.origin) {
        start = this.rotation.origin;
        // this.parent.prepareRotation(null, this.rotation.origin);
      } else if (this.parent.getType() == 'is') {
        const is = this.parent as ImportedShape;
        start = Object.values(is.controlPoints)[0].coords;
        is.prepareRotation(Object.keys(is.controlPoints)[0]);
      }

      const [sx, sy] = start;

      const [ex, ey] = end;
      const vector = new BaseVector([ex - sx, ey - sy]);
      vector.rotate(this.rotation.angle);

      // console.log('---------- startDrag - 1.1 --------------', vector);
      this.unMovedDiagonal = vector.length;

      this.unMoved = [vector.end.x, vector.end.y];
      this.unMovedAngle = vector.getAngle();

      console.log({ ex, ey, sx, sy });
      // console.log('unMovedAngle', this.unMovedAngle);
      // console.log('unMoved', this.unMoved);
      this.rotationMode = true;
    }

    this.config.startDrag?.(point);
  }

  pcDragByRotation(dx: number, dy: number, point: RCPoint) {
    let _dx: number, _dy: number;

    const [dw, dh] = this.getDwDhByRoation(dx, dy, point);

    const [dxOrigin, dyOrigin] = [dx, dy].map(val => val / 2);
    switch (point) {
      case 'left-top':
        this.newLeftTop = new BaseVector([
          this.leftTop.x + dx,
          this.leftTop.y + dy,
        ]);

        this.newShapePosition = new Vector(
          { x: dxOrigin, y: dyOrigin },
          {
            x: this.newLeftTop.x,
            y: this.newLeftTop.y,
          },
        ).rotate(-this.rotation);
        _dx = this.newShapePosition.end.x - this.shapePosition.x;
        _dy = this.newShapePosition.end.y - this.shapePosition.y;

        break;

      case 'left-bottom':
        this.newOrigin = [dxOrigin, dyOrigin];

        const [dLeftTopX, dLeftTopY] = [
          dw * Math.cos(this.rotation.angle),
          dw * Math.sin(this.rotation.angle),
        ];

        // The LEFT-TOP and the RIGHT-BOTTOM changes
        this.newLeftTop = new BaseVector([
          this.leftTop.x - dLeftTopX,
          this.leftTop.y - dLeftTopY,
        ]);

        this.newShapePosition = new Vector(
          { x: dxOrigin, y: dyOrigin },
          {
            x: this.newLeftTop.x,
            y: this.newLeftTop.y,
          },
        ).rotate(-this.rotation);

        _dx = this.newShapePosition.end.x - this.shapePosition.x;
        _dy = this.newShapePosition.end.y - this.shapePosition.y;

        break;
      case 'right-bottom':
        this.newOrigin = [dxOrigin, dyOrigin];

        this.newLeftTop = new BaseVector([
          this.leftTop.x - dxOrigin,
          this.leftTop.y - dyOrigin,
        ]);

        this.newLeftTop.rotate(-this.rotation);

        _dx = dxOrigin + this.newLeftTop.x - this.shapePosition.x;
        _dy = dyOrigin + this.newLeftTop.y - this.shapePosition.y;
        break;
      case 'right-top':
        // -- // -- //
        this.dLeftTop = [
          -dh * Math.cos(-this.rotation),
          -dh * Math.sin(-this.rotation),
        ];

        // console.log({ dxOrigin, dyOrigin });
        this.newLeftTop = new BaseVector([
          this.leftTop.x + this.dLeftTop[0],
          this.leftTop.y + this.dLeftTop[1],
        ]);

        this.newShapePosition = new Vector(
          { x: dxOrigin, y: dyOrigin },
          {
            x: this.newLeftTop.x,
            y: this.newLeftTop.y,
          },
        ).rotate(-this.rotation);

        _dx = this.newShapePosition.end.x - this.shapePosition.x;
        _dy = this.newShapePosition.end.y - this.shapePosition.y;

        break;
      default:
        break;
    }

    return [dw, dh, _dx, _dy];
  }

  getDwDhByRoation(dx: number, dy: number, point: RCPoint) {
    let angle = Math.atan(dy / dx);

    // -- // -- //
    if (dx < 0) {
      if (dy >= 0) {
        angle += Math.PI;
      } else {
        angle -= Math.PI;
      }
    }
    const diffAngle = angle - this.rotation.angle;
    const l = Math.sqrt(Math.pow(dx, 2) + Math.pow(dy, 2));

    // console.log('---------- getDwDh ------------', { l, diffAngle });
    const [dw, dh] = [Math.cos(diffAngle) * l, Math.sin(diffAngle) * l];

    switch (point) {
      case 'left-top':
        return [-dw, -dh];
      case 'left-bottom':
        return [-dw, dh];
      case 'right-bottom':
        return [dw, dh];
      case 'right-top':
        return [dw, -dh];
      default:
        return [0, 0];
    }
  }

  getDxDyByRotation(dx: number, dy: number, point: RCPoint) {
    // -- // -- // -- // -- // -- // -- //
  }

  hidePointControllers() {
    this.pcs.map(pc => pc.hide(true));
  }

  isMouseOver = false;

  mouseover() {
    if (this.inprogress) {
      return;
    }
    this.rcMouseOver = true;
    if (this.hiddenMode) {
      this.pcs.forEach(pc => pc.showAsGhost(0.5));
    }
    this.config.mouseover?.();
  }

  mouseout() {
    //setTimeout(() => {
    if (this.pcs.find(pc => pc.mouseIsOver)) {
      return;
    }

    if (this.dragIsInProgress) {
      return;
    }

    this.rcMouseOver = false;

    // if (this.innerMouseout) {
    //   this.innerMouseout = false;
    //   return;
    // }

    // it fires before some inner element's mouseover - so I need to wait
    // if I want to digest it
    setTimeout(() => {
      // if (this.innerMouseout) {
      //   return;
      // }
      // if (this.isMouseOver) {
      //   return;
      // }
      if (this.pcMouseOver || this.dragIsInProgress) {
        return;
      }
      if (this.hiddenMode) {
        this.pcs.forEach(pc => pc.showAsGhost());
      }
      this.config.mouseout?.();
    }, 1);
  }

  pcMouseOut() {
    // setTimeout(() => {
    // -- // -- // -- // -- //
    if (this.isMouseOver) {
      console.log('rc > isMouseOver yoooo!');
      return;
    }

    setTimeout(() => {
      if (this.isMouseOver) {
        return;
      }
      this.config.mouseout?.();
    }, 1);
    // -- // -- // -- // -- //
  }
  patch(config: Partial<RectangleControllerConfig>, refresh = false) {
    // -- //
    // console.log('---------- patch ----------', config);
    Object.keys(config).map(key => (this.config[key] = config[key]));

    // The width and height is updated in the case an ongoing animation
    if (config.width) {
      this.currentLengths[0] = this.width;
      this.saveUnmoved();
    }
    if (config.height) {
      this.currentLengths[1] = this.height;
      this.saveUnmoved();
    }

    if (config.width !== undefined && config.height !== undefined) {
      this.rect?.patch({
        'stroke-width': config['stroke-width'],
        width: config.width,
        height: config.height,
      });
    }

    if (config['stroke-width']) {
      // console.log('patch-stroke-width', config['stroke-width']);
      this.rect?.patch({
        'stroke-width': config['stroke-width'],
      });
    }

    const { offset, position } = config;

    if (offset) {
      this.container.setTransform(offset[0], offset[1]);
    }

    if (refresh) {
      this.refresh();
    }
  }

  prev: number;
  updateRectangle({
    width,
    height,
    x,
    y,
  }: {
    width?: number;
    height?: number;
    x?: number;
    y?: number;
  }) {
    if (width !== undefined) {
      this.width = width;
    }
    if (height !== undefined) {
      // if (!this.prev) {
      //   this.prev = this.height;
      // }

      // if (this.prev > height) {
      //   console.warn('decrease --------------');
      //   this.prev = height;
      // }

      this.height = height;
    }

    if (x !== undefined) {
      this.x = x;
    }

    if (y !== undefined) {
      this.x = x;
    }

    this.refresh();
  }

  hide({ onHover, full }: ShapeSelectParams = {}) {
    // console.log('------ rc.hide() -------', { onHover, full });
    // Dirthy hack but works - maybe I can work with the fill: transparent somehow
    // console.locg('rc > hide', this.parent.getType());
    // console.log('rc.hide', this.parent.getType());
    if (full) {
      this.container.visible = false;
      return;
    }
    /* console.log('rc-hide', {
      hiddenMode: this.hiddenMode,
      full,
    }); */
    if (this.hiddenMode && !full) {
      this.rect.patch({
        'stroke-width': 0,
        'fill-opacity': 0.001,
      });
      this.topElement?.hide();
      this.bottomElement?.hide();
    } else {
      this.container.visible = false;
    }

    if (onHover) {
      return;
    }
    if (this.hiddenMode) {
      this.pcs.forEach(pc => pc.showAsGhost());
    } else {
      this.pcs.map(pc => pc.hide());
    }
  }

  setNoneEventMode() {
    this.container.eventMode = 'none';
  }

  setAutEventMode() {
    this.container.eventMode = 'auto';
  }

  enableActions() {
    this.rect.enableActions();
  }

  // -- // -- //

  disableActions() {
    this.rect.disableActions();
  }

  setPreHoverState() {
    this.show();
    this.rect.patch({
      'stroke-width': 0,
      'fill-opacity': 0.001,
    });
  }

  show({ onHover, withoutPCs }: ShapeSelectParams = {}) {
    this.container.visible = true;
    // console.log('rc-show'); //
    this.refresh();

    if (this.hiddenMode) {
      this.rect.patch({
        'fill-opacity': this.config.fillOpacity || 0.001,
        'stroke-width':
          this.config['stroke-width'] == undefined
            ? this.strokeWidth
            : this.config['stroke-width'],
      });
      // console.log('rcs-show');
      this.pcs.map(pc => pc.show());
      // -- // -- //
    }
    this.rect.show();
    this.topElement?.show();
    this.bottomElement?.show();
    // -- //
    if (onHover) {
      // this.pcs.map(pc => pc.hide());
      return;
    }

    if (withoutPCs) {
      this.pcs.map(pc => pc.hide());
      return;
    }
    // if (onHover) {
    //   return;
    // }
    // console.log('rc.show', this.parent.getType());
    this.pcs.map(pc => pc.show());
    // this.zoomUpdate();
  }
  get visible() {
    return this.rect.visible;
  }
  remove() {
    this.container.destroy();
    this.pcs.map(pc => pc.remove());
  }

  drag(point: RCPoint, { dx, dy }: DragResponse) {
    this.mouseoutClear = true;
    this.cs.mouseOverclear = true;
    if (this.rotationMode) {
      const [x0, y0] = this.unMoved;
      const newVectorAngle = this.getAngle(x0 + dx, y0 + dy);
      return this.config.drag({
        angle: this.rotation.angle + newVectorAngle - this.unMovedAngle,
      });
    }

    if (this.symmetric) {
      if (dx > dy) {
        dy = dx;
      } else {
        dx = dy;
      }
    }

    this.pcDx = dx;
    this.pcDy = dy;
    const [w, h] = this.unMovedLengths;

    const newDx = dx;
    const newDy = dy;

    // const [newDx, vLines] = this.parent.checkHorizontal(
    //   [this.parent._x, this.parent._y, 0],
    //   [this.parent._x, this.parent._y + h, h],
    //   [dx, dy],
    // );

    // const [newDy, hLines] = this.parent.checkVertical(
    //   [this.parent._x, this.parent._y, 0],
    //   [this.parent._x + w, this.parent._y, w],
    //   [dx, dy],
    // );

    // if (vLines) {
    //   this.os.showVerticalOrientationLines(
    //     this.parent,
    //     vLines,
    //     newDx,
    //     newDy,
    //   );
    // }

    // if (hLines) {
    //   this.os.showHorizontalOrientationLines(
    //     this.parent,
    //     hLines,
    //     newDx,
    //     newDy,
    //   );
    // }

    // -- // -- // console.log('dx', this.dx, 'dy', this.dy, ''); // -- // -- // -- //

    if (this.rotation) {
      const [dw, dh, dxPos, dyPos] = this.pcDragByRotation(dx, dy, point);

      this.dw = dw;
      this.dh = dh;
      this.dx = dxPos;
      this.dy = dyPos;
    }

    switch (point) {
      case 'right-top':
        this.diff = [0, dy];
        if (this.centerBased) {
          this.dx = -newDx;
          this.dy = newDy;
          this.dw = newDx * 2;
          this.dh = -newDy * 2;
        } else {
          this.dx = 0;
          this.dy = newDy;
          this.dw = newDx;
          this.dh = -newDy;
        }
        break;

      case 'right-bottom':
        this.dx = 0;
        this.dy = 0;

        if (this.centerBased) {
          this.dx = -newDx;
          this.dy = -newDy;
          this.dw = newDx * 2;
          this.dh = newDy * 2;
        } else {
          this.dw = newDx;
          this.dh = newDy;
        }

        break;
      case 'left-top':
        this.dx = newDx;
        this.dy = newDy;
        if (this.centerBased) {
          this.dw = -newDx * 2;
          this.dh = -newDy * 2;
        } else {
          this.dw = -newDx;
          this.dh = -newDy;
        }
        break;
      case 'left-bottom':
        if (this.centerBased) {
          this.dx = newDx;
          this.dy = -newDy;
          this.dw = -newDx * 2;
          this.dh = newDy * 2;
        } else {
          this.dx = newDx;
          this.dy = 0;
          this.dw = -newDx;
          this.dh = newDy;
        }
        break;
      case 'left-center':
        // -- // -- //
        this.dx = dx;
        this.dy = 0;
        this.dw = this.centerBased ? -dx * 2 : -dx;
        this.dh = 0;
        break;
      case 'right-center':
        this.dx = this.centerBased ? -dx : 0;
        this.dy = 0;
        this.dw = this.centerBased ? dx * 2 : dx;
        this.dh = 0;
        break;
      case 'center-top':
        this.dx = 0;
        this.dy = dy;
        this.dh = this.centerBased ? -dy * 2 : -dy;
        break;
      case 'center-bottom':
        this.dx = 0;
        this.dy = this.centerBased ? -dy : 0;
        this.dh = this.centerBased ? dy * 2 : dy;
        this.pcCenterTop.patch({
          p: [this.offsetX + this.width / 2, this.offsetY + this.height],
        });
        break;
    }
    this.currentLengths = [w + this.dw, h + this.dh];

    if (this.scaleMode && this.isCornerPoint(point)) {
      const [w, h] = this.unMovedLengths;
      const scale = (w + this.dw) / w;
      this.dragResponse = {
        x: this.unMovedX + this.dx,
        y: this.unMovedY + this.dy,
        // width,
        // height,
        dx: this.dx,
        dy: this.dy,
        scale,
      };

      this.refreshRectangle(scale * w, scale * h);

      return this.config.drag?.(this.dragResponse);
    }

    const [width, height] = this.currentLengths;

    if (!this.config.noRectRefresh) {
      this.refreshRectangle(width, height, this.dx, this.dy);
    }

    // We need to update them because by their own they do not affect each other //

    // this.pcLeftTop.patch({ p: [this.x, this.y] }); //
    // this.pcRightTop.patch({ p: [this.x + width, this.y] }); //
    // this.pcRightBottom.patch({ p: [this.x + width, this.y + height] }); //
    // this.pcLeftBottom.patch({ p: [this.x, this.y + height] }); //

    return this.config.drag({
      x: this.unMovedX + this.dx,
      y: this.unMovedY + this.dy,
      width,
      height,
      dx: cloneDeep(this.dx),
      dy: cloneDeep(this.dy),
      dw: this.dw,
      dh: this.dh,
      pcDx: this.pcDx,
      pcDy: this.pcDy,
      point,
    });
  }

  getAngle(x: number, y: number) {
    if (!x && !y) {
      return 0;
    }
    const angle = Math.abs(Math.atan(y / x));
    if (x > 0 && y > 0) {
      return angle;
    }
    if (x < 0 && y > 0) {
      return Math.PI - angle;
    }
    if (x < 0 && y < 0) {
      return Math.PI + angle;
    }
    if (x > 0 && y < 0) {
      return 2 * Math.PI - angle;
    }
    if (Math.abs(x) === 0) {
      return y > 0 ? Math.PI / 2 : (3 * Math.PI) / 2;
    }
    if (Math.abs(y) === 0) {
      return x > 0 ? 0 : Math.PI;
    }
  }

  refresh() {
    const [width, height] = [this.width, this.height];

    this.refreshRectangle(width, height);

    this.updatePointControllers(width, height);

    // this.pcRightTop.patch({ p: [width, 0] }); //
    // this.pcRightBottom.patch({ p: [width, height] }); //
    // this.pcLeftBottom.patch({ p: [0, height] }); //

    this.pcs.map(pc => pc.refresh());
  }

  updatePointControllers(width: number, height: number, dx = 0, dy = 0) {
    // this.pcLeftTop.patch({ p: [this.x, this.y], pOffset: [-this.offsetX, -this.offsetY] });
    // this.pcRightTop.patch({ p: [this.x + width, this.y], pOffset: [-this.offsetX, -this.offsetY] });
    // this.pcRightBottom.patch({ p: [this.x + width, this.y + height], pOffset: [-this.offsetX, -this.offsetY] });
    // this.pcLeftBottom.patch({ p: [this.x, this.y + height], pOffset: [-this.offsetX, -this.offsetY] });

    // const [offsetX, offsetY] = [this.offsetX + dx, this.offsetY + dy];
    const [offsetX, offsetY] = [dx, dy];

    // --------- left --------- //
    this.pcLeftTop?.patch({
      p: [offsetX, offsetY],
      // pOffset,
    });

    this.pcLeftCenter?.patch({
      p: [offsetX, offsetY + height / 2],
      // pOffset,
    });

    this.pcLeftBottom?.patch({
      p: [offsetX, offsetY + height],
      // pOffset,
    });

    // --------- center --------- //

    this.pcCenterTop?.patch({
      p: [offsetX + width / 2, offsetY],
    });
    this.pcCenter?.patch({
      p: [offsetX + width / 2, offsetY + height / 2],
      // pOffset,
    });
    this.pcCenterBottom?.patch({
      p: [offsetX + width / 2, offsetY + height],
    });

    // --------- right --------- //
    this.pcRightCenter?.patch({
      p: [offsetX + width, offsetY + height / 2],
      // pOffset,
    });

    this.pcRightTop?.patch({
      p: [offsetX + width, offsetY],
      // pOffset,
    });

    this.pcRightBottom?.patch({
      p: [offsetX + width, offsetY + height],
      // pOffset,
    });
  }

  refreshRectangle(width: number, height: number, dx = 0, dy = 0) {
    let x = 0;
    let y = 0;
    if (width < 0) {
      x = width;
    }
    if (height < 0) {
      y = height;
    }

    // this.bottomElement?.patch({
    //   position: {
    //     x: 0,
    //     y: height,
    //   },
    // });

    // console.log('---------- refreshRectangle ----------', {});

    let strokeWidth = this.strokeWidth;

    if (this.config['stroke-width'] !== undefined) {
      strokeWidth = this.config['stroke-width'];
    }

    if (this.hiddenMode) {
      strokeWidth = 0;
    }

    if (this.dragIsInProgress) {
      strokeWidth = 1;
    }

    width = Math.abs(width);
    height = Math.abs(height);

    this.rect.patch({
      position: this.config.moveRectangle
        ? { x: x + dx, y: y + dy }
        : { x: x, y: y },
      width: Math.abs(width) || 0,
      height: Math.abs(height) || 0,
      'stroke-width': strokeWidth,
    });

    this.rect.element.hitArea = new Rectangle(
      -this.hitAreaMargin,
      -this.hitAreaMargin,
      width + this.hitAreaMargin * 2,
      height + this.hitAreaMargin * 2,
    );

    if (this.config.moveRectangle) {
      this.updatePointControllers(width, height, dx, dy);
    } else {
      this.updatePointControllers(width, height);
    }
  }

  saveUnmoved() {
    this.unMovedLengths = cloneDeep(this.currentLengths);
  }

  endDrag(point: RCPoint) {
    this.dragIsInProgress = false;
    this.mouseoutClear = false;
    this.cs.mouseOverclear = false;
    if (this.unMovedAngle) {
      // -- // -- // -- // -- // -- // -- //
    }

    this.width = this.unMovedLengths[0] + this.dw;
    this.height = this.unMovedLengths[1] + this.dh;
    this.pcDx = undefined;
    this.pcDy = undefined;
    this.diff = [0, 0];
    this.saveUnmoved();
    // -- // -- //

    if (this.config.moveRectangle) {
      this.unMovedX += this.pcDx;
      this.unMovedY += this.pcDy;
      this.offsetX += this.dx;
      this.offsetY += this.dy;
    }
    /* else {
      this.offsetX += this.dx;
      this.offsetY += this.dy;
    } */

    this.config.endDrag(point, { rotate: this.rotationMode });
    // this.parent.hideOrientationLines?.();
  }

  // _showShapeIcons() {
  //   if (this.shapeIconsContainer) {
  //     this.shapeIconsContainer.visible = true;
  //     return;
  //   }

  //   // Create container for icons
  //   this.shapeIconsContainer = new Container();
  //   this.container.addChild(this.shapeIconsContainer);

  //   const iconSize = 24;
  //   const iconSpacing = 24;
  //   const padding = 8;
  //   const totalWidth = iconSize * 3 + iconSpacing * 2;
  //   const backgroundWidth = totalWidth + padding * 2;
  //   const backgroundHeight = iconSize + padding * 2;

  //   // Calculate center position
  //   const centerX = this.width / 2 - backgroundWidth / 2;
  //   const centerY = this.height / 2 - backgroundHeight / 2;

  //   // Background rectangle
  //   const background = new Graphics();
  //   background.beginFill(0x000000, 0.7);
  //   background.lineStyle(1, 0xffffff, 1);
  //   background.drawRoundedRect(
  //     centerX,
  //     centerY,
  //     backgroundWidth,
  //     backgroundHeight,
  //     4,
  //   );
  //   background.endFill();
  //   this.shapeIconsContainer.addChild(background);

  //   // Icon positions (evenly spaced)
  //   const icon1X = centerX + padding + iconSize / 2;
  //   const icon2X = centerX + padding + iconSize + iconSpacing + iconSize / 2;
  //   const icon3X =
  //     centerX + padding + iconSize * 2 + iconSpacing * 2 + iconSize / 2;
  //   const iconY = centerY + padding + iconSize / 2;

  //   // 1) Text icon: "T" using a Goldman bitmap font
  //   /* this.textIcon = new BitmapText('T', {
  //     fontName: 'Goldman', // must match <info face="..."> in your .fnt
  //     fontSize: 24,
  //     // tint is supported via "tint" on the display object (not in style)
  //   });
  //   this.textIcon.tint = 0xffffff;
  //   centerAnchorLike(this.textIcon);
  //   this.textIcon.position.set(icon1X, iconY);
  //   this.textIcon.eventMode = 'static';
  //   this.textIcon.cursor = 'pointer';
  //   this.textIcon.on('pointerdown', () => {
  //     this.config.onTextIconClick?.();
  //   });
  //   this.shapeIconsContainer.addChild(this.textIcon); */

  //   // 2) Image icon from Material Icons bitmap font (use codepoint, not ligature)
  //   this.imageIcon = new BitmapText(MI_CODEPOINTS.image, {
  //     fontName: 'MaterialIconsOutlined',
  //     fontSize: 30,
  //   });
  //   this.imageIcon.tint = 0xffffff;
  //   centerAnchorLike(this.imageIcon);
  //   this.imageIcon.position.set(icon2X, iconY + 4);
  //   this.imageIcon.eventMode = 'static';
  //   this.imageIcon.cursor = 'pointer';
  //   this.imageIcon.on('pointerdown', () => {
  //     this.config.onImageIconClick?.();
  //   });
  //   this.shapeIconsContainer.addChild(this.imageIcon);

  //   // 3) Animated images icon (smart_display) from Material Icons bitmap font
  //   this.animatedImagesIcon = new BitmapText(MI_CODEPOINTS.smart_display, {
  //     fontName: 'MaterialIconsOutlined',
  //     fontSize: 30,
  //   });
  //   this.animatedImagesIcon.tint = 0xffffff;
  //   centerAnchorLike(this.animatedImagesIcon);
  //   this.animatedImagesIcon.position.set(icon3X, iconY + 4);
  //   this.animatedImagesIcon.eventMode = 'static';
  //   this.animatedImagesIcon.cursor = 'pointer';
  //   this.animatedImagesIcon.on('pointerdown', () => {
  //     this.config.onAnimatedImagesIconClick?.();
  //   });
  //   this.shapeIconsContainer.addChild(this.animatedImagesIcon);

  //   // BitmapText doesn't depend on async webfont loading, so no setTimeout needed.
  //   this.shapeIconsContainer.visible = true;
  // }

  innerMouseout = false;

  showShapeIcons() {
    if (this.shapeIconsContainer) {
      this.shapeIconsContainer.visible = true;
      return;
    }

    this.shapeIconsContainer = new Container();
    this.shapeIconsContainer.sortableChildren = true; // ensure zIndex works, prevents overlap quirks
    this.container.addChild(this.shapeIconsContainer);

    const iconSize = 24;
    const iconSpacing = 12;
    const padding = 8;
    const totalWidth = iconSize * 3 + iconSpacing * 2;
    const backgroundWidth = totalWidth + padding * 2;
    const backgroundHeight = iconSize + padding * 2;

    // Center in world coords
    const centerX = this.width / 2 - backgroundWidth / 2;
    const centerY = this.height / 2 - backgroundHeight / 2;

    // --- Background (LOCAL drawing, GLOBAL positioning) ---
    const background = new Graphics();
    background.eventMode = 'static'; // v7 style
    // background.interactive = true;              // not needed in v7

    // Draw at (0,0) in LOCAL space
    background.beginFill(0x000000, 0.7);
    background.lineStyle(1, 0xffffff, 1);
    background.drawRoundedRect(0, 0, backgroundWidth, backgroundHeight, 4);
    background.endFill();

    // Position the whole graphics in GLOBAL space
    background.position.set(centerX, centerY);

    // Give a LOCAL hitArea that matches the drawn shape
    // background.hitArea = new RoundedRectangle(
    //   0,
    //   0,
    //   backgroundWidth,
    //   backgroundHeight,
    //   4,
    // );

    // background.zIndex = 0;
    this.shapeIconsContainer.addChild(background);

    // Robust hover state on the background itself
    background.on('mouseenter', e => {
      // console.log('innerMouseout = true');
      this.innerMouseout = true;
      e.stopPropagation();
    });
    background.on('mouseleave', e => {
      this.innerMouseout = false;
      e.stopPropagation();
    });
    // background.on('mouseover', e => {
    //   console.log('innerMouseout = true');
    //   this.innerMouseout = true;
    //   e.stopPropagation();
    // });
    // background.on('mouseout', e => {
    //   this.innerMouseout = false;
    //   console.log('innerMouseout = false');
    //   e.stopPropagation();
    // });

    // --- Icon positions (relative to same global layout you had) ---
    const icon1X = centerX + padding + iconSize / 2;
    const icon2X = centerX + padding + iconSize + iconSpacing + iconSize / 2;
    const icon3X =
      centerX + padding + iconSize * 2 + iconSpacing * 2 + iconSize / 2;
    const iconY = centerY + padding + iconSize / 2;

    // --- Helper to stop hover bubbling from icons ---
    const wireIcon = (displayObject: DisplayObject, onClick?: () => void) => {
      displayObject.eventMode = 'static';
      (displayObject as any).cursor = 'pointer';

      if (onClick) {
        displayObject.on('pointerdown', (e: FederatedPointerEvent) => {
          onClick();
          // If you also attach click to background and want to prevent it:
          // e.stopPropagation();
        });
      }

      // Stop hover bubbling only (prevents background leave/enter flicker)
      displayObject.on('mouseenter', (e: FederatedPointerEvent) =>
        e.stopPropagation(),
      );
      displayObject.on('mouseleave', (e: FederatedPointerEvent) =>
        e.stopPropagation(),
      );
      displayObject.on('pointerover', (e: FederatedPointerEvent) =>
        e.stopPropagation(),
      );
      displayObject.on('pointerout', (e: FederatedPointerEvent) =>
        e.stopPropagation(),
      );

      (displayObject as any).zIndex = 1; // over the background
    };

    // --- Icons ---
    this.textIcon = new Text('T', {
      fontFamily: 'Goldman',
      fontSize: 24,
      fill: 0xffffff,
      fontWeight: 'bold',
      stroke: '#ffffff',
      strokeThickness: 1,
    });
    this.textIcon.anchor.set(0.5);
    this.textIcon.position.set(icon1X, iconY);
    this.shapeIconsContainer.addChild(this.textIcon);
    wireIcon(this.textIcon, () => this.config.onTextIconClick?.());

    const imageTexture = Texture.from('/assets/icons/image.png', {
      resourceOptions: { crossOrigin: 'anonymus' },
    });
    this.imageIcon = new Sprite(imageTexture);
    this.imageIcon.anchor.set(0.5);
    this.imageIcon.position.set(icon2X, iconY);
    this.shapeIconsContainer.addChild(this.imageIcon);
    wireIcon(this.imageIcon, () => this.config.onImageIconClick?.());

    const videoIconTexture = Texture.from('/assets/icons/video.png', {
      resourceOptions: { crossOrigin: 'anonymus' },
    });
    this.animatedImagesIcon = new Sprite(videoIconTexture);
    this.animatedImagesIcon.anchor.set(0.5);
    this.animatedImagesIcon.position.set(icon3X, iconY);
    this.shapeIconsContainer.addChild(this.animatedImagesIcon);
    wireIcon(this.animatedImagesIcon, () =>
      this.config.onAnimatedImagesIconClick?.(),
    );

    this.shapeIconsContainer.visible = true;
  }

  // showShapeIcons() {
  //   // -- //
  //   if (this.shapeIconsContainer) {
  //     this.shapeIconsContainer.visible = true;
  //     return;
  //   }

  //   // Create container for icons
  //   this.shapeIconsContainer = new Container();

  //   this.container.addChild(this.shapeIconsContainer);

  //   const iconSize = 24;
  //   const iconSpacing = 12;
  //   const padding = 8;
  //   const totalWidth = iconSize * 3 + iconSpacing * 2;
  //   const backgroundWidth = totalWidth + padding * 2;
  //   const backgroundHeight = iconSize + padding * 2;

  //   // Calculate center position
  //   const centerX = this.width / 2 - backgroundWidth / 2;
  //   const centerY = this.height / 2 - backgroundHeight / 2;

  //   // Create black background rectangle using Graphics
  //   const background = new Graphics();

  //   background.interactive = true;
  //   background.eventMode = 'static';
  //   background.beginFill(0xff0000, 0.7);
  //   background.lineStyle(1, 0xffffff, 1);
  //   background.drawRoundedRect(
  //     centerX,
  //     centerY,
  //     backgroundWidth,
  //     backgroundHeight,
  //     4,
  //   );
  //   background.endFill();
  //   this.shapeIconsContainer.addChild(background);

  //   background.on('mouseenter', (e: FederatedPointerEvent) => {
  //     console.log('innerMouseout = true');
  //     this.innerMouseout = true;
  //   });
  //   background.on('mouseleave', (e: FederatedPointerEvent) => {
  //     console.log('innerMouseout = false');
  //     this.innerMouseout = false;
  //   });
  //   // Calculate icon positions (evenly spaced)
  //   const icon1X = centerX + padding + iconSize / 2;
  //   const icon2X = centerX + padding + iconSize + iconSpacing + iconSize / 2;
  //   const icon3X =
  //     centerX + padding + iconSize * 2 + iconSpacing * 2 + iconSize / 2;
  //   const iconY = centerY + padding + iconSize / 2;

  //   const wireIcon = (displayObject: DisplayObject, onClick?: () => void) => {
  //     displayObject.eventMode = 'static';
  //     (displayObject as any).cursor = 'pointer';

  //     if (onClick) {
  //       displayObject.on('pointerdown', (e: FederatedPointerEvent) => {
  //         onClick();
  //         // If the background also listens for clicks and you don't want it to fire:
  //         // e.stopPropagation();
  //       });
  //     }

  //     // Stop only hover-related events from bubbling to the background
  //     displayObject.on('mouseenter', (e: FederatedPointerEvent) =>
  //       e.stopPropagation(),
  //     );
  //     displayObject.on('mouseleave', (e: FederatedPointerEvent) =>
  //       e.stopPropagation(),
  //     );
  //     displayObject.on('pointerover', (e: FederatedPointerEvent) =>
  //       e.stopPropagation(),
  //     );
  //     displayObject.on('pointerout', (e: FederatedPointerEvent) =>
  //       e.stopPropagation(),
  //     );
  //   };

  //   // Create text icon (T letter)
  //   this.textIcon = new Text('T', {
  //     fontFamily: 'Goldman',
  //     fontSize: 24,
  //     fill: 0xffffff,
  //     fontWeight: 'bold',
  //     stroke: '#ffffff',
  //     strokeThickness: 1,
  //   });
  //   this.textIcon.anchor.set(0.5);
  //   this.textIcon.position.set(icon1X, iconY);
  //   this.textIcon.eventMode = 'static';
  //   this.textIcon.cursor = 'pointer';
  //   this.textIcon.on('pointerdown', () => {
  //     this.config.onTextIconClick?.();
  //   });
  //   this.shapeIconsContainer.addChild(this.textIcon);

  //   // Create image icon (using Material Icons font)

  //   wireIcon(this.textIcon, () => this.config.onTextIconClick?.());

  //   const imageTexture = Texture.from('/assets/icons/image.png', {
  //     resourceOptions: {
  //       crossOrigin: 'anonymus',
  //     },
  //   });

  //   this.imageIcon = new Sprite(imageTexture);

  //   // this.imageIcon = new Text('image', {
  //   //   fontFamily: 'Material Icons Outlined',
  //   //   fontSize: 30,
  //   //   fill: 0xffffff,
  //   //   stroke: '#ffffff',
  //   //   strokeThickness: 0.5,
  //   // });
  //   this.imageIcon.anchor.set(0.5);
  //   this.imageIcon.position.set(icon2X, iconY);
  //   this.imageIcon.eventMode = 'static';
  //   this.imageIcon.cursor = 'pointer';
  //   this.imageIcon.on('pointerdown', () => {
  //     this.config.onImageIconClick?.();
  //   });
  //   this.shapeIconsContainer.addChild(this.imageIcon);

  //   wireIcon(this.imageIcon, () => this.config.onImageIconClick?.());

  //   // Create animated images icon (using Material Icons font)

  //   const videoIconTexture = Texture.from('/assets/icons/video.png', {
  //     resourceOptions: {
  //       crossOrigin: 'anonymus',
  //     },
  //   });

  //   this.animatedImagesIcon = new Sprite(videoIconTexture);
  //   // new Text('smart_display', {
  //   //   fontFamily: 'Material Icons Outlined',
  //   //   fontSize: 30,
  //   //   fill: 0xffffff,
  //   //   stroke: '#ffffff',
  //   //   strokeThickness: 0.5,
  //   // });
  //   this.animatedImagesIcon.anchor.set(0.5);
  //   this.animatedImagesIcon.position.set(icon3X, iconY);
  //   this.animatedImagesIcon.eventMode = 'static';
  //   this.animatedImagesIcon.cursor = 'pointer';
  //   this.animatedImagesIcon.on('pointerdown', () => {
  //     this.config.onAnimatedImagesIconClick?.();
  //   });
  //   this.shapeIconsContainer.addChild(this.animatedImagesIcon);

  //   wireIcon(this.animatedImagesIcon, () =>
  //     this.config.onAnimatedImagesIconClick?.(),
  //   );

  //   this.shapeIconsContainer.visible = true;
  // }

  hideShapeIcons() {
    if (this.shapeIconsContainer) {
      this.shapeIconsContainer.visible = false;
    }
  }

  zoomUpdate() {
    this.rect?.patch({
      'stroke-width': this.hiddenMode ? 0 : this.strokeWidth,
    });
    this.topElement?.patch({
      hoverItem: {
        type: 'line',
        x: this.width,
        y: 0,
        height: 8 / this.cs.canvasScale,
      },
    });
    this.bottomElement?.patch({
      hoverItem: {
        type: 'line',
        x: this.width,
        y: 0,
        height: 8 / this.cs.canvasScale,
      },
    });
    this.pcs.map(pc => pc.zoomUpdate());
  }
}
